import type { HierarchyNode } from "./agency-hierarchy";

/**
 * Downloads for the agency org chart: a PNG of the rendered tree and a CSV of the rows
 * behind it.
 *
 * ZERO NEW DEPENDENCIES. `html2canvas` and `html-to-image` are both absent from
 * package.json, and neither is worth ~50kB of bundle plus a transitive-vulnerability
 * surface for one button, so the PNG is rasterised with the platform primitive instead:
 * clone the subtree -> inline its computed CSS -> wrap it in an SVG <foreignObject> ->
 * draw that SVG into a <canvas> -> toBlob(). If either library is ever added for another
 * feature, `rasterise()` below is the only function that needs to change.
 *
 * Known limits of the foreignObject approach — accepted, not oversights:
 *  - An SVG loaded through an <img> cannot fetch external sub-resources, so images are
 *    pre-inlined as data URIs here and any that fail (CORS, 404) are dropped rather than
 *    rendered as broken-image glyphs. `background-image: url(...)` is NOT inlined.
 *  - Webfonts are not embedded. The computed `font-family` keeps the full declared stack,
 *    so text falls back to the next available family — metrics shift slightly, layout does
 *    not break.
 *  - `::before` / `::after` content and <canvas> pixels do not survive the clone.
 *  - Everything is same-origin by construction, so the canvas is never tainted and
 *    `toBlob()` is always permitted.
 */

/**
 * The CSV takes flat rows. `HierarchyTreeNode` extends `HierarchyNode`, so the nested tree
 * flattened by the caller satisfies this too — no separate overload needed.
 */
export type HierarchyRow = HierarchyNode;

/* -------------------------------------------------------------------------- */
/* PNG                                                                         */
/* -------------------------------------------------------------------------- */

/** Elements that carry no pixels but bloat (or corrupt) the serialised SVG. */
const NON_VISUAL_TAGS = new Set(["SCRIPT", "NOSCRIPT", "LINK", "META", "STYLE"]);

/**
 * Rasterise `element` to a PNG and hand it to the browser as a download.
 *
 * The capture is taken from the element's *untransformed* layout box (`scrollWidth` /
 * `scrollHeight`), not its on-screen rect: an org chart is usually panned and zoomed, and
 * an owner clicking "Export" wants the whole tree at 100%, not whatever fraction of it is
 * currently in the viewport.
 *
 * Rejects with a descriptive Error on every failure path — no dangling promise, nothing
 * thrown from a callback the caller cannot catch.
 */
export async function exportHierarchyPng(element: HTMLElement, fileName: string): Promise<void> {
  assertBrowser("exportHierarchyPng");

  const rect = element.getBoundingClientRect();
  const width = Math.max(element.scrollWidth, Math.ceil(rect.width), 1);
  const height = Math.max(element.scrollHeight, Math.ceil(rect.height), 1);

  const blob = await rasterise(element, width, height);
  triggerDownload(blob, withExtension(fileName, "png"));
}

async function rasterise(element: HTMLElement, width: number, height: number): Promise<Blob> {
  const clone = element.cloneNode(true) as HTMLElement;

  // Order matters: styles are copied by walking source and clone in lockstep by index, so
  // nothing may be added or removed from the clone until that walk is done.
  copyComputedStyles(element, clone);
  stripNonVisual(clone);
  await inlineImages(clone);
  normaliseRoot(clone, width, height);

  const svg = serialiseToSvg(clone, width, height);

  const canvas = document.createElement("canvas");
  // Cap at 2x. Retina panels report 2–3, and a 3x bitmap of a wide org chart is tens of
  // megabytes for detail nobody looking at a PNG will ever see.
  const scale = Math.min(Math.max(window.devicePixelRatio || 1, 1), 2);
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not export the chart: this browser blocked 2D canvas.");
  ctx.scale(scale, scale);

  // foreignObject renders on transparency; a transparent PNG of dark-mode text is
  // unreadable in most image viewers, so paint the chart's own background under it.
  ctx.fillStyle = resolveBackgroundColor(element);
  ctx.fillRect(0, 0, width, height);

  const image = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
  ctx.drawImage(image, 0, 0, width, height);

  return canvasToBlob(canvas);
}

/**
 * Copy every computed property onto the clone as an inline style.
 *
 * The SVG is rendered in an isolated document that cannot see the page's stylesheets, so
 * anything not inlined here simply does not exist at raster time. Copying the *computed*
 * declaration (rather than trying to serialise matching rules) is the only approach that
 * survives Tailwind, CSS variables and cascade layers unchanged.
 */
function copyComputedStyles(source: Element, clone: Element): void {
  const computed = window.getComputedStyle(source);
  let css = "";
  for (let i = 0; i < computed.length; i++) {
    const prop = computed.item(i);
    css += `${prop}:${computed.getPropertyValue(prop)};`;
  }
  clone.setAttribute("style", css);

  const sourceChildren = source.children;
  const cloneChildren = clone.children;
  for (let i = 0; i < sourceChildren.length; i++) {
    const s = sourceChildren.item(i);
    const c = cloneChildren.item(i);
    if (s && c) copyComputedStyles(s, c);
  }
}

function stripNonVisual(root: HTMLElement): void {
  root.querySelectorAll("*").forEach((node) => {
    if (NON_VISUAL_TAGS.has(node.tagName)) node.remove();
  });
}

/**
 * Turn every <img> into a data URI, dropping the ones that cannot be fetched.
 *
 * Best-effort by design: agency logos and owner avatars are nice to have in the export,
 * but a single 404 or a CORS-hostile CDN must never fail the whole download.
 */
async function inlineImages(root: HTMLElement): Promise<void> {
  const images = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    images.map(async (img) => {
      const src = img.getAttribute("src");
      if (!src || src.startsWith("data:")) return;
      try {
        const res = await fetch(src, { mode: "cors" });
        if (!res.ok) throw new Error(String(res.status));
        img.setAttribute("src", await blobToDataUrl(await res.blob()));
      } catch {
        img.remove();
      }
    }),
  );
}

/**
 * Pin the clone to the capture box.
 *
 * Its own transform is dropped (the caller's pan/zoom lives on this node; children keep
 * theirs) and overflow is opened up so a scrolled-out branch is exported rather than
 * clipped at the visible edge.
 */
function normaliseRoot(clone: HTMLElement, width: number, height: number): void {
  clone.style.transform = "none";
  clone.style.margin = "0";
  clone.style.overflow = "visible";
  clone.style.width = `${width}px`;
  clone.style.height = `${height}px`;
  clone.style.maxWidth = "none";
  clone.style.maxHeight = "none";
}

function serialiseToSvg(clone: HTMLElement, width: number, height: number): string {
  // XMLSerializer, not outerHTML: foreignObject content must be well-formed XML, and HTML
  // serialisation leaves void elements like <br> and <img> unclosed.
  const markup = new XMLSerializer().serializeToString(clone);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" ` +
    `viewBox="0 0 ${width} ${height}">` +
    `<foreignObject width="100%" height="100%">${markup}</foreignObject>` +
    `</svg>`
  );
}

/** Nearest opaque ancestor background, so the export matches the theme it was taken in. */
function resolveBackgroundColor(element: HTMLElement): string {
  let node: HTMLElement | null = element;
  while (node) {
    const color = window.getComputedStyle(node).backgroundColor;
    if (color && color !== "transparent" && !color.startsWith("rgba(0, 0, 0, 0)")) return color;
    node = node.parentElement;
  }
  return "#ffffff";
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error("Could not export the chart: the rendered image failed to load."));
    image.src = src;
  });
}

/**
 * `toBlob` has been universal for years but is still absent from a few embedded and
 * locked-down WebViews, where the property is simply undefined. Fall back to the far older
 * `toDataURL` and decode it by hand rather than letting the export die on a TypeError.
 */
function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Could not export the chart: the browser produced no image data."));
      }, "image/png");
      return;
    }

    if (typeof canvas.toDataURL !== "function") {
      reject(new Error("Could not export the chart: this browser cannot save canvas images."));
      return;
    }

    try {
      resolve(dataUrlToBlob(canvas.toDataURL("image/png")));
    } catch {
      reject(new Error("Could not export the chart: this browser cannot save canvas images."));
    }
  });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, payload] = dataUrl.split(",");
  if (!header || payload === undefined) throw new Error("Malformed data URL");
  const mime = header.match(/:(.*?);/)?.[1] ?? "image/png";
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Could not read image data"));
    reader.readAsDataURL(blob);
  });
}

/* -------------------------------------------------------------------------- */
/* CSV                                                                         */
/* -------------------------------------------------------------------------- */

const CSV_COLUMNS = [
  "Agency",
  "Parent Agency",
  "Depth",
  "Owner",
  "Agents",
  "Sold Premium",
  "Underwriting Premium",
  "Production",
  "Sold Policies",
  "Underwriting Policies",
] as const;

/**
 * Download the hierarchy as a spreadsheet-ready CSV.
 *
 * Money is written as bare numbers with no currency symbol or thousands separator — the
 * first thing anyone does with this file is SUM a column, and "$1,234" imports as text.
 */
export function exportHierarchyCsv(rows: HierarchyRow[], fileName: string): void {
  assertBrowser("exportHierarchyCsv");

  // `parentAgencyId` is all the API gives us, so parent NAMES are resolved against the
  // exported set itself.
  const namesById = new Map(rows.map((row) => [row.id, row.name]));

  const lines = [CSV_COLUMNS.map(csvCell).join(",")];
  for (const row of rows) {
    lines.push(
      [
        csvCell(row.name),
        csvCell(resolveParentLabel(row, namesById)),
        csvCell(row.depth),
        csvCell(row.ownerName || row.ownerEmail || ""),
        csvCell(row.agentCount),
        csvCell(row.soldPremium),
        csvCell(row.underwritingPremium),
        csvCell(row.production),
        csvCell(row.soldPolicies),
        csvCell(row.underwritingPolicies),
      ].join(","),
    );
  }

  // RFC 4180 line ending, plus a UTF-8 BOM: without it Excel on Windows renders agency
  // names with accented characters as mojibake.
  const csv = `\uFEFF${lines.join("\r\n")}\r\n`;
  triggerDownload(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
    withExtension(fileName, "csv"),
  );
}

/**
 * Orphans (parent outside the exported subtree, or deleted) keep their raw id rather than
 * being blanked — same stance as `buildHierarchyTree`, which promotes orphans to roots
 * instead of hiding them. A blank here would read as "this is a root", which is a lie.
 */
function resolveParentLabel(row: HierarchyRow, namesById: Map<string, string>): string {
  if (!row.parentAgencyId) return "";
  return namesById.get(row.parentAgencyId) ?? row.parentAgencyId;
}

/** Leading characters Excel and Sheets treat as the start of a formula. */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/**
 * RFC 4180 escaping: quote any field containing a comma, quote or newline, and double the
 * embedded quotes.
 *
 * Numbers bypass the formula guard on purpose — a legitimately negative premium starts
 * with "-", and neutralising it would import the whole column as text.
 */
function csvCell(value: string | number): string {
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "0";
  }

  // Agency and owner names are user-supplied and land in a file people open in Excel, so a
  // leading "=" is a formula-injection vector. Prefixing an apostrophe is the standard
  // neutralisation and is invisible in the cell.
  const guarded = FORMULA_LEAD.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

/* -------------------------------------------------------------------------- */
/* Shared                                                                      */
/* -------------------------------------------------------------------------- */

function assertBrowser(fn: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new Error(`${fn} is browser-only and was called during server rendering.`);
  }
}

function withExtension(fileName: string, extension: string): string {
  const trimmed = fileName.trim() || `agency-hierarchy`;
  return trimmed.toLowerCase().endsWith(`.${extension}`) ? trimmed : `${trimmed}.${extension}`;
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  // The download is claimed synchronously by the click, so the handle can go on the next
  // macrotask. Revoking in the same tick aborts the download in some WebKit builds, and
  // never revoking leaks the whole blob for the life of the document.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}
